import { blocklist } from '@wyn/content';
import { assertGame } from './errors.ts';

/**
 * The word filter (S1 phase 6): the blocklist in `@wyn/content`, refused on the server in names
 * and chat, and its general swearing starred out of chat. Text is folded first: lower case, accents
 * dropped, look-alike characters mapped to the letter they imitate (`0→o`, `1→i`, `@→a`, `$→s`, Cyrillic `а→a`…), and anything else a gap.
 * Repeated letters are let through by the patterns themselves (`niiigger` matches `nigger`), which,
 * unlike collapsing the text, keeps "niger" apart from it.
 *
 * Names refuse an `anywhere` entry wherever it falls. Chat matches whole words only, so "Scunthorpe",
 * "assassin" and "Penistone" pass. A word spelt out a letter at a time ("n i g g e r") is read as one.
 * Swearing in chat is starred out, unless it's spelt out like that: then the line is refused.
 */

const LOOKALIKES: Record<string, string> = {
  '0': 'o', '1': 'i', '!': 'i', '|': 'i', '3': 'e', '4': 'a', '@': 'a', '5': 's', '$': 's', '7': 't', '+': 't', '8': 'b', '9': 'g', '€': 'e',
  // Cyrillic and Greek letters drawn like Latin ones.
  а: 'a', в: 'b', е: 'e', ё: 'e', і: 'i', ї: 'i', к: 'k', м: 'm', н: 'h', о: 'o', р: 'p', с: 'c', т: 't', у: 'y', х: 'x',
  α: 'a', β: 'b', ε: 'e', ι: 'i', κ: 'k', ν: 'v', ο: 'o', ρ: 'p', τ: 't', υ: 'u', χ: 'x',
};

/** Text as the filter reads it: lower-case a–z words separated by single spaces. */
export function foldForFilter(raw: string): string {
  // `!` and `|` stand for `i` within a word, not at its end ("shit!").
  const s = raw.normalize('NFKD').replace(/\p{M}|\p{Cf}/gu, '').toLowerCase().replace(/[!|]+(?![\p{L}\p{N}])/gu, ' ');
  return [...s]
    .map((ch) => LOOKALIKES[ch] ?? ch)
    .join('')
    .replace(/[^a-z]+/g, ' ')
    .trim();
}

/** `nigger` → `n+i+g{2,}e+r+`: each letter may repeat, but a double stays at least double. */
const pattern = (entry: string) =>
  entry
    .replace(/\*$/, '')
    .match(/(.)\1*/g)!
    .map((run) => (run.length === 1 ? `${run}+` : `${run[0]}{${run.length},}`))
    .join('');

/** A whole word: `x*` entries by their start, the rest as written or with a plural `s` or `es`. */
function wordRegex(entries: readonly string[]): RegExp {
  const prefix = entries.filter((e) => e.endsWith('*')).map(pattern);
  const exact = entries.filter((e) => !e.endsWith('*')).map(pattern);
  const alts = [...(prefix.length ? [`(?:${prefix.join('|')})`] : []), ...(exact.length ? [`(?:${exact.join('|')})(?:e?s+)?$`] : [])];
  return new RegExp(`^(?:${alts.join('|')})`);
}

const { swearing } = blocklist;
const anywhereRe = new RegExp(blocklist.anywhere.map(pattern).join('|'));
const anyWordRe = wordRegex([...blocklist.anywhere, ...blocklist.wordOnly]);
const nameAnywhereRe = new RegExp([...blocklist.anywhere, ...swearing.anywhere].map(pattern).join('|'));
const nameWordRe = wordRegex([...blocklist.wordOnly, ...swearing.wordOnly]);
const swearWordRe = wordRegex([...swearing.anywhere, ...swearing.wordOnly]);

/** The words of folded text, with each run of single letters joined into one ("n i g" → "nig"). */
function words(folded: string): { word: string; spelt: boolean }[] {
  const out: { word: string; spelt: boolean }[] = [];
  let run = '';
  const flush = () => {
    if (run.length > 1) out.push({ word: run, spelt: true });
    run = '';
  };
  for (const w of folded.split(' ').filter(Boolean)) {
    if (w.length === 1) run += w;
    else {
      flush();
      out.push({ word: w, spelt: false });
    }
  }
  flush();
  return out;
}

/** Whether a line of chat carries a blocked word. A letter-at-a-time run is searched within, as that spelling is deliberate. */
export function chatHasBlockedWord(text: string): boolean {
  return words(foldForFilter(text)).some(({ word, spelt }) => anyWordRe.test(word) || (spelt && (anywhereRe.test(word) || swearWordRe.test(word))));
}

/** Chat with each sworn word starred out after its first letter: "oh sh1t!" → "oh s***!". */
export function maskSwearing(text: string): string {
  return text.replace(/\S+/g, (token) => {
    const [, lead, body, tail] = token.match(/^(['"‘“([]*)(.*?)([.,;:?!'"’”)\]]*)$/su)!;
    if (!body || !foldForFilter(body).split(' ').some((w) => swearWordRe.test(w))) return token;
    const [first, ...rest] = [...body];
    return `${lead}${first}${'*'.repeat(rest.length)}${tail}`;
  });
}

/** Whether a name carries a blocked or sworn word: an `anywhere` entry inside it (spaces and hyphens ignored), or a `wordOnly` one as a whole word. */
export function nameHasBlockedWord(name: string): boolean {
  const folded = foldForFilter(name);
  return nameAnywhereRe.test(folded.replace(/ /g, '')) || words(folded).some(({ word }) => nameWordRe.test(word));
}

export function assertWelcomeChat(text: string): void {
  assertGame(!chatHasBlockedWord(text), 'INVALID', 'That word isn’t welcome here.');
}

export function assertWelcomeName(name: string): void {
  assertGame(!nameHasBlockedWord(name), 'INVALID', 'That name isn’t welcome here. Choose another.');
}
